/**
 * Wisetack compliance (low-code integration guide §1): the phrase "lending
 * partners" must link to /lenders and "wisetack.com/faqs" must link to /faqs.
 * The visible copy never changes — the phrase itself becomes the link — and
 * only the first "lending partners" is anchored, so the later mention inside
 * "Not all merchants and lending partners…" stays plain.
 *
 * Every HTML surface that ships the disclosure resolves these rules from here:
 * the public estimate/invoice pages through the React component, and the
 * merchant's "Send email" through buildWisetackDisclosureHtml. PDF surfaces
 * cannot hyperlink and print both URLs as visible text instead (see
 * WisetackPdfBlock).
 */
export const WISETACK_DISCLOSURE_ANCHORS: ReadonlyArray<{
  phrase: string;
  href: string;
  once: boolean;
}> = [
  { phrase: 'lending partners', href: 'https://www.wisetack.com/lenders', once: true },
  { phrase: 'wisetack.com/faqs', href: 'https://www.wisetack.com/faqs', once: false },
];

/** Wisetack's floor for the disclosure, whatever a payload asks for. */
export const WISETACK_DISCLOSURE_MIN_FONT_PX = 11;

/**
 * Short copy approved for estimate/invoice emails (Notifi Feedback v3, pp. 8, 13).
 * Keep in sync with operations tenants/wisetack-settings.util.ts.
 */
export const WISETACK_APPROVED_EMAIL_DISCLOSURE_TEXT =
  '*All financing is subject to credit approval. Your terms may vary. ' +
  'Payment options through Wisetack are provided by our lending partners. ' +
  'See https://www.wisetack.com/faqs.';

export function wisetackDisclosureFontSize(minimumFontSizePx?: number | null): number {
  return Math.max(minimumFontSizePx ?? 0, WISETACK_DISCLOSURE_MIN_FONT_PX);
}

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (character) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[character] ?? character,
  );
}

/**
 * The disclosure as an email-ready paragraph: escaped, hyperlinked per the
 * rules above, and never below Wisetack's font floor. Anchors are inserted
 * after escaping so the markup we add survives and the copy we received cannot
 * inject any of its own.
 */
export function buildWisetackDisclosureHtml(disclosure: {
  text: string;
  minimumFontSizePx?: number | null;
}): string {
  const fontSize = wisetackDisclosureFontSize(disclosure.minimumFontSizePx);
  const body = WISETACK_DISCLOSURE_ANCHORS.reduce((html, anchor) => {
    // The approved email copy prints the full FAQ URL; keep that visible URL
    // inside one anchor while preserving the public disclosure's bare label.
    if (anchor.phrase === 'wisetack.com/faqs') {
      return html.replace(
        /(?:https:\/\/www\.)?wisetack\.com\/faqs/g,
        (label) => `<a href="${anchor.href}">${label}</a>`,
      );
    }
    const link = `<a href="${anchor.href}">${anchor.phrase}</a>`;
    // String.replace hits the first occurrence, which is exactly the "once"
    // rule; replaceAll covers the phrases Wisetack wants linked every time.
    return anchor.once ? html.replace(anchor.phrase, link) : html.replaceAll(anchor.phrase, link);
  }, escapeHtml(disclosure.text));

  return `<p style="font-size:${fontSize}px;line-height:1.4;margin-top:16px">${body}</p>`;
}
